/**
 * Checkout redirect to the expansion store.
 *
 * Ported from Concept's assets/custom-checkout-redirect.js, 2026-09-23. The
 * request body and the endpoint are unchanged from ShapeTech's version — see
 * snippets/pruvit-checkout-redirect.liquid for what may and may not be edited,
 * and for the two deliberate differences from the original.
 */
document.addEventListener('DOMContentLoaded', function () {
  const currency = window.ShopLocale?.currency || window.Shopify?.currency?.active || 'USD';

  async function generateAndRedirect() {
    const cartRes = await fetch(window.Shopify.routes.root + 'cart.js', {
      headers: { Accept: 'application/json' },
    });
    const cart = await cartRes.json();

    const payload = {
      currency,
      items: cart.items,
      token: cart.token,
      isUserLoggedIn: window.ShapeTech.isUserLoggedIn,
      noteAttributes: cart.attributes,
      countryCode: window?.ShopLocale?.countryCode || window?.Shopify?.country,
      languageCode: window?.ShopLocale?.language || window?.Shopify?.locale,
      customerEmail: window.ShapeTech?.customerEmail ?? undefined,
    };

    const res = await fetch(`${stAppConfig.apiRoot}/cart/sca/generate-cart`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const shopLocale = window?.ShopLocale?.language || window?.Shopify?.locale;
    const { data, message } = await res.json();
    if (!data) throw new Error(message ?? 'Missing redirect URL from generate-cart');

    let redirectPath = data;
    const [base, returnTo] = redirectPath.split('return_to=');
    if (returnTo) {
      // Their comment, kept: intentionally split rather than includes, to
      // handle a link carrying "?" with no parameters after it.
      const parts = returnTo.split('?');
      const updated =
        parts.length > 1 && parts[1] !== ''
          ? `${parts[0]}?${parts[1]}&locale=${shopLocale}`
          : `${parts[0]}?locale=${shopLocale}`;
      redirectPath = `${base}return_to=${encodeURIComponent(updated)}`;
    } else {
      redirectPath += (redirectPath.includes('?') ? '&' : '?') + 'locale=' + shopLocale;
    }

    window.location.href = redirectPath;
  }

  // Re-entry guard. The original set `trigger.disabled`, which does nothing on
  // an anchor — and this theme's checkout control is one, so a second click
  // during the round trip would have started a second cart.
  let running = false;

  document.body.addEventListener('click', async (event) => {
    const trigger = event.target.closest('.custom_checkout_button_JS');
    if (!trigger) return;

    // No config means no apiRoot to post to. Let the anchor's own href take
    // them to this store's checkout rather than swallowing the click.
    if (typeof stAppConfig === 'undefined' || !stAppConfig?.apiRoot) return;

    event.preventDefault();
    if (running) return;
    running = true;
    // [loading] is what the CSS paints; aria-busy is what a screen reader
    // hears. The buy box CTA uses the same attribute for the same state.
    trigger.setAttribute('loading', '');
    trigger.setAttribute('aria-busy', 'true');

    try {
      await generateAndRedirect();
      // Deliberately no cleanup on the way out. generateAndRedirect sets
      // location.href and returns immediately, while the browser is still
      // fetching the next page — a `finally` here flipped the button back to
      // "Continue to checkout" during exactly the wait this state exists to
      // cover, which is the longest part of it. The state ends when the page
      // does.
      return;
    } catch (err) {
      // Deliberately does NOT fall through to the anchor's href. The first
      // draft did, on the grounds that a dead control is worse than a wrong
      // one — which is backwards here. The redirect exists to put the order on
      // the store that serves this shopper's market, so quietly checking them
      // out on this one is the outcome the whole mechanism is there to
      // prevent, and it would look like a success. Concept alerts and stays
      // put; so does this.
      console.error('[pruvit] checkout redirect failed:', err);
      alert(err?.message || pruvitT('checkoutUnavailable', 'Checkout is unavailable right now. Please try again.'));
    }

    // Only reached when the redirect failed, which is the one case where the
    // shopper stays on this page and needs the button back.
    running = false;
    trigger.removeAttribute('loading');
    trigger.removeAttribute('aria-busy');
  });
});
